/**
 * Vanilla dropdown module — replaces Bootstrap 3 dropdown JS.
 *
 * Supports two modes:
 *   <button data-dropdown-toggle="menuId"> → opens #menuId, toggles .open on its .dropdown/.dropup parent
 *   <button data-dropdown-toggle>          → toggles .open on the closest .dropdown/.dropup ancestor
 *
 * Falls back to the trigger's parentElement when no .dropdown/.dropup wrapper exists
 * (e.g. notifications panel that uses custom CSS with #notifications.open).
 *
 * CSS contract (unchanged from Bootstrap 3):
 *   .open > .dropdown-menu { display: block; }
 */

// Guard against double-initialisation when multiple packs import this module
// (e.g. legacy.js + client_rendering.js) and Rspack evaluates it more than once.
if ((window as any).__dropdownInitialised) {
  // already running — bail out so listeners aren't registered twice
} else {
(window as any).__dropdownInitialised = true;

const CONTAINER_SELECTOR = ".dropdown, .dropup";
const TOGGLE_ATTR = "data-dropdown-toggle";
const TOGGLE_SELECTOR = `[${TOGGLE_ATTR}]`;

/** Track every container we've opened so closeAll works for non-standard wrappers too. */
const openContainers = new Set<HTMLElement>();

function findContainer(trigger: HTMLElement): HTMLElement | null {
  const targetId = trigger.getAttribute(TOGGLE_ATTR)?.trim();

  if (targetId && targetId.length > 0) {
    const menu = document.getElementById(targetId);
    if (!menu) return null;
    return menu.closest<HTMLElement>(CONTAINER_SELECTOR) ?? menu.parentElement;
  }

  return trigger.closest<HTMLElement>(CONTAINER_SELECTOR) ?? trigger.parentElement;
}

function closeAll(except?: HTMLElement | null): void {
  // Snapshot to avoid mutating Set during iteration
  const toClose = [...openContainers].filter((c) => c !== except);
  for (const container of toClose) {
    container.classList.remove("open");
    container.querySelector<HTMLElement>(TOGGLE_SELECTOR)?.setAttribute("aria-expanded", "false");
    openContainers.delete(container);
  }

  // Also catch containers opened externally (e.g. jQuery .addClass('open'))
  document.querySelectorAll<HTMLElement>(`${CONTAINER_SELECTOR}.open`).forEach((container) => {
    if (container === except) return;
    container.classList.remove("open");
    container.querySelector<HTMLElement>(TOGGLE_SELECTOR)?.setAttribute("aria-expanded", "false");
  });
}

function toggle(trigger: HTMLElement): void {
  const container = findContainer(trigger);
  if (!container) return;

  const willOpen = !container.classList.contains("open");

  // Close every other open dropdown first
  closeAll(willOpen ? container : null);

  container.classList.toggle("open", willOpen);
  trigger.setAttribute("aria-expanded", String(willOpen));

  if (willOpen) {
    openContainers.add(container);
  } else {
    openContainers.delete(container);
  }
}

// --- listeners (delegated on document — works for AJAX-injected content) ---

document.addEventListener("click", (e: MouseEvent) => {
  const trigger = (e.target as HTMLElement).closest<HTMLElement>(TOGGLE_SELECTOR);

  if (trigger) {
    e.preventDefault();
    toggle(trigger);
    return;
  }

  // Click landed outside any trigger — close all open dropdowns,
  // unless the click is inside a container we opened (let it propagate).
  const target = e.target as Node;
  let insideTracked = false;
  openContainers.forEach((container) => {
    if (container.contains(target)) insideTracked = true;
  });
  if (!insideTracked) {
    closeAll();
  }
});

document.addEventListener("keydown", (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    closeAll();
  }
});

} // end of double-init guard
